import { WarningFilled } from "@ant-design/icons"
import { Button, Flex, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { showErrorNotifications } from "../../../utils/handlers/handle-error"
import { contractValidationHasWarnings } from "./calculate-contract-validation.ts"
import { formatDurationLine, formatSalesPriceLine } from "./format-contract-validation.ts"
import type { ContractValidationResult } from "./types.ts"

const { Text, Title } = Typography

interface ContractValidationConfirmModalProps {
  open: boolean
  /** Go Back & Fix — closes the modal only. The Contract's own edits were
   * already saved by the PATCH that runs before this modal opens (same
   * convention as `ContractGenerateConfirmModal`), so nothing here needs to
   * undo anything — the user just lands back in the editor. */
  onClose: () => void
  result: ContractValidationResult | null
  /** Generate Anyway — proceeds through the EXISTING generate flow
   * unchanged (the same sync-check step that runs when there are no
   * validation warnings at all). This modal never alters that flow, only
   * gates entry to it. */
  onProceed: () => void | Promise<void>
}

/**
 * Shown only when a Generate-time recheck (the authoritative backend
 * `GET .../validation/` call, against the just-saved data) finds the
 * Contract's own numbers don't add up — a plain-language guardrail, never a
 * blocker: "Generate Anyway" always remains available.
 */
function ContractValidationConfirmModal({
  open,
  onClose,
  result,
  onProceed
}: ContractValidationConfirmModalProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  if (!result || !contractValidationHasWarnings(result)) return null

  const lines = [
    formatSalesPriceLine(t, result.sales_price),
    formatDurationLine(t, result.duration)
  ].filter((line): line is NonNullable<typeof line> => !!line?.comparison)

  const handleGenerateAnyway = async () => {
    try {
      setSaving(true)
      await onProceed()
      onClose()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      maskClosable={false}
      centered
      width={560}
      data-testid={"contract-validation-confirm-modal"}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("contractValidation.reviewModal.title")}
        </Title>
      }
      footer={[
        <Button key={"goBack"} disabled={saving} onClick={onClose}>
          {t("contractValidation.reviewModal.goBack")}
        </Button>,
        <Button
          key={"generateAnyway"}
          type={"primary"}
          loading={saving}
          onClick={handleGenerateAnyway}
        >
          {t("contractValidation.reviewModal.generateAnyway")}
        </Button>
      ]}
    >
      <Text>
        {t("contractValidation.reviewModal.summary", { count: lines.length })}
      </Text>

      <Flex vertical gap={12} className={"mt-4"}>
        {lines.map((line, index) => (
          <Flex
            key={index}
            gap={8}
            align={"flex-start"}
            data-testid={"contract-validation-confirm-line"}
          >
            <WarningFilled style={{ color: "#faad14", marginTop: 2 }} />
            <Flex vertical>
              <Text strong>{line.title}</Text>
              <Text type={"secondary"}>{line.comparison}</Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Modal>
  )
}

export { ContractValidationConfirmModal }
